import React, { useCallback, useContext, useMemo } from "react";
import { CheckboxField, HeaderB } from "components";
import lang from "translations";
import { Path } from "paths";
import { FragmentA } from "components/fragments";

import {
  Field,
  Input,
  Panel,
  ActionButton,
  Toast,
  Form,
  Skeleton,
} from "components/commons";

import { ModuleWrapper } from "components/fragments";
import { useForm, useModal, useRouter } from "hooks";
import { VenueContext } from "contexts";
import { mixpanel, TrackEvent } from "mixpanel";

import initialFormState from "./resource-type.form-state";

const ResourceTypeForm = ({
  submit,
  title,
  submitting,
  initialState = undefined,
  deleteResourceTypeButton,
  loading,
  error,
}) => {
  const { venue } = useContext(VenueContext);
  const { history } = useRouter();
  const { venueId } = venue;

  const formState = useMemo(() => {
    return initialFormState(initialState);
  }, [initialState]);

  const unsaveChangesModal = useModal();

  const {
    fields,
    modifyField,
    submitForm,
    getFormValues,
    applyFieldErrors,
    dirty,
  } = useForm({
    initialState: formState,
  });

  /** Navigate Back */
  const goToList = useCallback(() => {
    history.push(Path.RESOURCETYPE);
  }, [history]);

  /** Leave Page */
  const leavePage = useCallback(() => {
    if (dirty) {
      unsaveChangesModal.show({
        ok: () => {
          goToList();
          unsaveChangesModal.close();
        },
      });
      return;
    }
    goToList();
  }, [dirty, unsaveChangesModal, goToList]);

  /** Submit Handler */
  const handleSubmit = useCallback(async () => {
    const params = getFormValues();

    const obj = {
      name: params.name,
      defaultCapacity: params.defaultCapacity,
      defaultCleanupDuration: params.defaultCleanupDuration,
      shape: params.shape,
      defaultReservationDuration: params.defaultReservationDuration,
      minimumSpentAmount: params.minimumSpentAmount,
      isActive: params.isActive,
    };

    try {
      mixpanel.track(TrackEvent.Clickedbutton, {
        Button: lang.saveResourceTypeForm,
        Page: title,
      });

      const res = await submit({ ...obj, venueId });

      Toast({
        content: res.message,
        success: true,
        icon: "check",
      }).open();

      history.push(Path.RESOURCETYPE);
    } catch ({ code, handleError }) {
      if (code === 3003) {
        applyFieldErrors({
          name: lang.resourceTypeNameAlreadyExist,
        });
      } else {
        handleError();
      }
    }
  }, [submit, getFormValues, venueId, history, applyFieldErrors, title]);

  /** Submit Trigger */
  const submitFormValue = () => {
    submitForm(handleSubmit);
  };

  return (
    <ModuleWrapper
      error={error}
      header={
        <HeaderB
          title={title}
          returnText={lang.resourceTypes}
          onClick={leavePage}
        />
      }
    >
      <Form
        unsaveChangesModal={unsaveChangesModal}
        onSubmit={submitFormValue}
        isPrompt={dirty}
      >
        <FragmentA title={lang.basicInfo}>
          <Panel>
            {loading ? (
              <Skeleton />
            ) : (
              <div>
                {/* Name */}
                <Field label={fields.name.label} {...fields.name}>
                  <Input required {...fields.name} onChange={modifyField} />
                </Field>

                {/* Default Capacity */}
                <Field label={fields.defaultCapacity.label} {...fields.defaultCapacity}>
                  <Input
                    required
                    {...fields.defaultCapacity}
                    onChange={(name, { value }) => {
                      if (!/^\d*$/.test(value)) return;
                      modifyField(name, { value });
                    }}
                  />
                </Field>

                {/* Default Cleanup Duration */}
                <Field
                  label={fields.defaultCleanupDuration.label}
                  {...fields.defaultCleanupDuration}
                >
                  <Input
                    required
                    {...fields.defaultCleanupDuration}
                    onChange={(name, { value }) => {
                      if (!/^\d*$/.test(value)) return;
                      modifyField(name, { value });
                    }}
                  />
                </Field>

                {/* Shape */}
                <Field label={fields.shape.label} {...fields.shape}>
                  <Input required {...fields.shape} onChange={modifyField} />
                </Field>

                {/* Default Reservation Duration */}
                <Field
                  label={fields.defaultReservationDuration.label}
                  {...fields.defaultReservationDuration}
                >
                  <Input
                    required
                    {...fields.defaultReservationDuration}
                    onChange={(name, { value }) => {
                      if (!/^\d*$/.test(value)) return;
                      modifyField(name, { value });
                    }}
                  />
                </Field>

                {/* Minimum Spent Amount */}
                <Field label={fields.minimumSpentAmount.label} {...fields.minimumSpentAmount}>
                  <Input
                    required
                    {...fields.minimumSpentAmount}
                    onChange={(name, { value }) => {
                      if (!/^\d*$/.test(value)) return;
                      modifyField(name, { value });
                    }}
                  />
                </Field>

                {/* Active */}
                <Field label={fields.isActive.label} {...fields.isActive}>
                  <CheckboxField {...fields.isActive} onChange={modifyField} />
                </Field>
              </div>
            )}
          </Panel>
        </FragmentA>

        {/* Buttons */}
        <ActionButton
          showLine
          loading={submitting}
          primary={{
            onClick: () => submitFormValue(),
          }}
          secondary={{
            onClick: () => leavePage(),
          }}
          danger={deleteResourceTypeButton}
        />
      </Form>
    </ModuleWrapper>
  );
};

export default ResourceTypeForm;
